import { useState, useEffect, useCallback } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';

export interface TripPurpose {
  purposeId: number;
  purposeLabel: string;
}

interface UseTripPurposeReturn {
  tripPurposes: TripPurpose[];
  tripPurposeOptions: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useTravelPurpose = (): UseTripPurposeReturn => {
  const [tripPurposes, setTripPurposes] = useState<TripPurpose[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTravelPurpose = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<{
        success: boolean;
        tripPurposes: TripPurpose[];
      }>(
        '/api/trip-purposes?includeDeleted=false',
        {},
        30 * 60 * 1000 // 30 minutes TTL - purposes rarely change
      );

      if (response.success && response.tripPurposes) {
        // sort A → Z by purposeLabel
        const sortedPurposes = response.tripPurposes.sort(
          (a: TripPurpose, b: TripPurpose) => a.purposeLabel.localeCompare(b.purposeLabel)
        );

        setTripPurposes(sortedPurposes);
        console.log("Fetched travel purposes (cached/sorted):", sortedPurposes);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching trip purpose:', err);
      setError('Failed to fetch trip purpose');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed since api and setters are stable

  useEffect(() => {
    fetchTravelPurpose();
  }, [fetchTravelPurpose]); // ✅ Now includes the dependency

  // Convert trip purposes to travel purpose options format for dropdowns (already sorted)
  const tripPurposeOptions = tripPurposes.map(tp => tp.purposeLabel);

  return {
    tripPurposes,
    tripPurposeOptions,
    loading,
    error,
    refetch: fetchTravelPurpose
  };
};